/**
 * 일정 캘린더 날짜 계산 — KST 고정 산술 순수 함수.
 *
 * ⚠ import 금지 — scripts/test-calendar-date.js가 typescript.transpileModule로 통째로 평가해 node에서 검증한다.
 * ⚠ 기기 시간대를 믿지 않는다. 회사가 한국이라 화면은 언제나 KST로 그린다 — 해외 출장 노트북이 다른 시간대여도
 *   일정 칸이 하루 밀리지 않게 `ms + 9h` 뒤 getUTC*로 읽는다. 이 파일에서 new Date(y,m,d)·getHours()를 쓰지 말 것.
 * ⚠ 반복 회차 전개는 서버(server/services/calendarTime.js) 하나뿐이다 — 여기서 펼치지 않는다.
 *   표시 문구(formatOccurrence·repeatSummary)만 서버와 같은 규칙의 사본이다(런타임 이미지에 src/가 없어 공유 불가).
 *   한쪽 문구를 바꾸면 다른 쪽도 맞출 것.
 */

export const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
export const DAY_MS = 24 * 60 * 60 * 1000;
export const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toMs(v: number | string | Date): number {
  if (typeof v === 'number') return v;
  if (v instanceof Date) return v.getTime();
  return new Date(v).getTime();
}

export function isValidDateKey(key: unknown): key is string {
  if (typeof key !== 'string') return false;
  const m = DATE_KEY_RE.exec(key);
  if (!m) return false;
  const y = +m[1], mo = +m[2], d = +m[3];
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, mo, 0)).getUTCDate();
}

/** KST 날짜 키의 자정 → UTC ms */
export function dateKeyToMs(key: string): number {
  const m = DATE_KEY_RE.exec(key);
  if (!m) return NaN;
  return Date.UTC(+m[1], +m[2] - 1, +m[3]) - KST_OFFSET_MS;
}

export function kstDateKey(v: number | string | Date): string {
  const d = new Date(toMs(v) + KST_OFFSET_MS);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export interface KstParts { y: number; m: number; d: number; hh: number; mm: number; dow: number }

export function kstParts(v: number | string | Date): KstParts {
  const d = new Date(toMs(v) + KST_OFFSET_MS);
  return {
    y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(),
    hh: d.getUTCHours(), mm: d.getUTCMinutes(), dow: d.getUTCDay(),
  };
}

export function addDaysKey(key: string, n: number): string {
  return kstDateKey(dateKeyToMs(key) + n * DAY_MS);
}

export function todayKey(nowMs: number = Date.now()): string {
  return kstDateKey(nowMs);
}

export function keyParts(key: string): { y: number; m: number; d: number; dow: number } {
  const p = kstParts(dateKeyToMs(key));
  return { y: p.y, m: p.m, d: p.d, dow: p.dow };
}

/** 'YYYY-MM' 달의 이전·다음 */
export function shiftMonth(y: number, m: number, delta: number): { y: number; m: number } {
  const total = (m - 1) + delta;
  return { y: y + Math.floor(total / 12), m: (((total % 12) + 12) % 12) + 1 };
}

/** 월 격자 42칸(6주) — 일요일 시작. 그 달 1일이 들어 있는 주의 일요일부터 */
export function monthGridKeys(y: number, m: number): string[] {
  const first = `${y}-${pad2(m)}-01`;
  const dow = keyParts(first).dow;
  const start = addDaysKey(first, -dow);
  const out: string[] = [];
  for (let i = 0; i < 42; i += 1) out.push(addDaysKey(start, i));
  return out;
}

/** 월 격자의 조회 범위 — from(포함)·to(배타) 날짜 키. 홈 미니 캘린더·월 보기·목록 보기가 같은 키를 써서 캐시를 공유한다 */
export function monthGridRange(y: number, m: number): { from: string; to: string } {
  const keys = monthGridKeys(y, m);
  return { from: keys[0], to: addDaysKey(keys[41], 1) };
}

export function monthLabel(y: number, m: number): string {
  return `${y}년 ${m}월`;
}

// ─── 표시 문구 (server/services/calendarTime.js와 같은 규칙) ─────────────

export function formatTimeKo(hh: number, mm: number): string {
  const ampm = hh < 12 ? '오전' : '오후';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${ampm} ${h12}:${pad2(mm)}`;
}

export function formatDateKo(key: string, withYear = false): string {
  const p = keyParts(key);
  return `${withYear ? `${p.y}년 ` : ''}${p.m}월 ${p.d}일(${WEEKDAY_KO[p.dow]})`;
}

export function formatOccurrence(start: string | number, end: string | number, allDay: boolean, nowMs: number = Date.now()): string {
  const sMs = toMs(start), eMs = toMs(end);
  const s = kstParts(sMs);
  const withYear = s.y !== kstParts(nowMs).y;
  const sKey = kstDateKey(sMs);
  if (allDay) {
    const lastKey = addDaysKey(kstDateKey(eMs), -1);
    const e = keyParts(lastKey);
    const head = formatDateKo(sKey, withYear);
    if (lastKey === sKey) return head;
    if (e.y !== s.y) return `${head}~${formatDateKo(lastKey, true)}`;
    if (e.m !== s.m) return `${head}~${e.m}월 ${e.d}일(${WEEKDAY_KO[e.dow]})`;
    return `${head}~${e.d}일(${WEEKDAY_KO[e.dow]})`;
  }
  const e = kstParts(eMs);
  const head = `${formatDateKo(sKey, withYear)} ${formatTimeKo(s.hh, s.mm)}`;
  if (sKey === kstDateKey(eMs)) {
    const tail = formatTimeKo(e.hh, e.mm);
    return `${head}~${(s.hh < 12) === (e.hh < 12) ? tail.slice(3) : tail}`;
  }
  return `${head}~${formatDateKo(kstDateKey(eMs), e.y !== s.y)} ${formatTimeKo(e.hh, e.mm)}`;
}

/**
 * 목록 한 줄의 시간 칸 — 그날 기준.
 *  종일 → '종일' · 같은 날 시간 → '오후 2:00' (+끝) · 전날부터 이어짐 → '~오전 10:00' · 다음날로 넘어감 → '오후 11:00~'
 */
export function timeLabelForDay(occ: { start: string; end: string; allDay: boolean }, dayKey: string): { main: string; sub: string } {
  if (occ.allDay) return { main: '종일', sub: '' };
  const s = kstParts(occ.start), e = kstParts(occ.end);
  const sKey = kstDateKey(occ.start);
  const eKey = kstDateKey(toMs(occ.end) - 1);
  if (sKey === dayKey && eKey === dayKey) return { main: formatTimeKo(s.hh, s.mm), sub: formatTimeKo(e.hh, e.mm) };
  if (sKey === dayKey) return { main: formatTimeKo(s.hh, s.mm), sub: '다음 날까지' };
  if (eKey === dayKey) return { main: '~' + formatTimeKo(e.hh, e.mm), sub: '' };
  return { main: '종일', sub: '' };
}

export function repeatSummary(start: string | number, freq: string | null | undefined, until?: string | null): string {
  if (!freq) return '';
  const p = kstParts(start);
  let head: string;
  switch (freq) {
    case 'daily': head = '매일'; break;
    case 'weekly': head = `매주 ${WEEKDAY_KO[p.dow]}요일`; break;
    case 'monthly': head = `매월 ${p.d}일`; break;
    case 'yearly': head = `매년 ${p.m}월 ${p.d}일`; break;
    default: return '';
  }
  if (!until) return head;
  const u = keyParts(until);
  return `${head} · ${u.y}년 ${u.m}월 ${u.d}일까지`;
}

// ─── 입력 변환 (편집기) ────────────────────────────────────────────────

/** ISO → KST 'HH:mm' */
export function toTimeInput(v: string | number): string {
  const p = kstParts(v);
  return `${pad2(p.hh)}:${pad2(p.mm)}`;
}

/** KST 날짜 키 + 'HH:mm' → '+09:00' ISO (서버가 그대로 파싱한다) */
export function kstIso(dateKey: string, hhmm: string): string {
  return `${dateKey}T${hhmm}:00+09:00`;
}

/** 다음 정시 (KST) — 새 일정의 기본 시작 */
export function nextHourMs(nowMs: number = Date.now()): number {
  const hour = 60 * 60 * 1000;
  return Math.floor(nowMs / hour) * hour + hour; // KST는 정수 시간 오프셋이라 UTC 정시 = KST 정시
}

export const REMIND_OPTIONS_TIMED: Array<{ value: number; label: string }> = [
  { value: 0, label: '시작 시간' },
  { value: 10, label: '10분 전' },
  { value: 30, label: '30분 전' },
  { value: 60, label: '1시간 전' },
  { value: 1440, label: '하루 전' },
];
export const REMIND_OPTIONS_ALL_DAY: Array<{ value: number; label: string }> = [
  { value: -540, label: '당일 오전 9시' },
  { value: 900, label: '전날 오전 9시' },
];

export function remindLabel(offset: number | null | undefined, allDay: boolean): string {
  if (offset === null || offset === undefined) return '알림 없음';
  const list = allDay ? REMIND_OPTIONS_ALL_DAY : REMIND_OPTIONS_TIMED;
  return list.find(o => o.value === offset)?.label || '알림';
}

// ─── 격자 배치 ─────────────────────────────────────────────────────────

export interface DayItem<T> { item: T; continuesBefore: boolean; continuesAfter: boolean }

/**
 * 날짜별 회차 목록 — 여러 날에 걸친 회차는 걸친 날마다 들어간다. 정렬: 종일·여러 날 먼저, 그다음 시작 시각.
 * 목록 보기·모바일 선택한 날·홈 카드 목록·월 격자 점이 같은 결과를 쓴다.
 */
export function itemsByDay<T extends { startDate: string; endDate: string; allDay: boolean; start: string }>(
  occs: T[], fromKey: string, toKeyExclusive: string,
): Record<string, DayItem<T>[]> {
  const out: Record<string, DayItem<T>[]> = {};
  for (const o of occs) {
    let k = o.startDate < fromKey ? fromKey : o.startDate;
    let guard = 0;
    while (k <= o.endDate && k < toKeyExclusive && guard < 400) {
      (out[k] || (out[k] = [])).push({ item: o, continuesBefore: k !== o.startDate, continuesAfter: k !== o.endDate });
      k = addDaysKey(k, 1);
      guard += 1;
    }
  }
  for (const k of Object.keys(out)) {
    out[k].sort((a, b) => {
      const am = a.item.allDay || a.item.startDate !== a.item.endDate ? 0 : 1;
      const bm = b.item.allDay || b.item.startDate !== b.item.endDate ? 0 : 1;
      if (am !== bm) return am - bm;
      return a.item.start < b.item.start ? -1 : a.item.start > b.item.start ? 1 : 0;
    });
  }
  return out;
}

export interface WeekBar<T> {
  item: T;
  /** 0~6 */
  col: number;
  span: number;
  row: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/**
 * 월 격자 한 주(7칸)의 막대 배치 — 여러 날 회차는 한 줄 막대로 이어 그린다.
 * 줄은 maxRows까지만 그리고 넘치는 것은 날마다 '+N'으로 센다(hiddenByCol).
 */
export function layoutWeek<T extends { key: string; startDate: string; endDate: string; allDay: boolean; start: string }>(
  weekKeys: string[], occs: T[], maxRows = 3,
): { bars: WeekBar<T>[]; hiddenByCol: number[] } {
  const first = weekKeys[0], last = weekKeys[weekKeys.length - 1];
  const inWeek = occs.filter(o => o.startDate <= last && o.endDate >= first);
  // 여러 날 → 긴 것 → 시작 이른 것 순으로 줄을 배정한다
  const sorted = [...inWeek].sort((a, b) => {
    const am = a.startDate !== a.endDate ? 0 : 1, bm = b.startDate !== b.endDate ? 0 : 1;
    if (am !== bm) return am - bm;
    const as = a.startDate < first ? first : a.startDate, bs = b.startDate < first ? first : b.startDate;
    if (as !== bs) return as < bs ? -1 : 1;
    const al = a.allDay ? 0 : 1, bl = b.allDay ? 0 : 1;
    if (al !== bl) return al - bl;
    return a.start < b.start ? -1 : a.start > b.start ? 1 : 0;
  });
  const occupied: boolean[][] = []; // row → col → 사용 중
  const bars: WeekBar<T>[] = [];
  const hiddenByCol = [0, 0, 0, 0, 0, 0, 0];
  for (const o of sorted) {
    const s = o.startDate < first ? first : o.startDate;
    const e = o.endDate > last ? last : o.endDate;
    const col = weekKeys.indexOf(s);
    const endCol = weekKeys.indexOf(e);
    if (col < 0 || endCol < 0) continue;
    const span = endCol - col + 1;
    let row = 0;
    for (;; row += 1) {
      if (!occupied[row]) occupied[row] = [];
      let free = true;
      for (let c = col; c <= endCol; c += 1) if (occupied[row][c]) { free = false; break; }
      if (free) break;
    }
    if (row >= maxRows) {
      for (let c = col; c <= endCol; c += 1) hiddenByCol[c] += 1;
      continue;
    }
    for (let c = col; c <= endCol; c += 1) occupied[row][c] = true;
    bars.push({ item: o, col, span, row, continuesBefore: o.startDate < first, continuesAfter: o.endDate > last });
  }
  return { bars, hiddenByCol };
}
